import React, { useEffect, useMemo, useState } from "react";
import { Table } from "antd";
import SwNoData from "../NoData/NoData";
import SwLoader from "../SwLoader/SwLoader";

const AntDTable = ({
  columns = [],
  dataSource = [],
  rowKey = "id",

  loading = false,

  /* Pagination */
  pagination = true,
  pageSize = 25,
  currentPage,
  totalRecords,
  onPageChange,
  showTotal = true,

  /* Sorting */
  clientSideSorting = false,

  onChange,
  showSorterTooltip = { target: "sorter-icon" },
  sortIcon, // pass `() => null` to hide the sort caret icons

  /* Table behavior */
  rowSelection,
  expandable,
  bordered = true,
  size = "small", // 👈 SW-UI default size, small, middle
  scrollX = "100%", // 👈 SW-UI default responsive scroll --- max-content"
  scroll,
  summary,
  className = "",
  onRowClick,
}) => {
  const [sorterState, setSorterState] = useState(null);
  const [internalPageSize, setInternalPageSize] = useState(pageSize);
  const [internalCurrentPage, setInternalCurrentPage] = useState(1);

  /* Sync internal pageSize if prop changes externally */
  useEffect(() => {
    setInternalPageSize(pageSize);
  }, [pageSize]);

  /* ---------- Client-side Sorting ---------- */
  const sortedData = useMemo(() => {
    if (!clientSideSorting || !sorterState?.order) {
      return dataSource;
    }

    const { field, order } = sorterState;

    return [...dataSource].sort((a, b) => {
      if (a[field] < b[field]) return order === "ascend" ? -1 : 1;
      if (a[field] > b[field]) return order === "ascend" ? 1 : -1;
      return 0;
    });
  }, [dataSource, sorterState, clientSideSorting]);

  /* ---------- Normalize Columns (SW-UI layer) ---------- */
  const normalizedColumns = useMemo(() => {
    return columns.map((col) => {
      let updatedCol = { ...col };

      let headerStyle = {};
      let cellStyle = {};

      /* ---------- No Wrap Support ---------- */
      if (col.noWrap) {
        headerStyle.whiteSpace = "nowrap";
      }

      /* ---------- Min Width Support ---------- */
      if (col.minWidth) {
        headerStyle.minWidth = col.minWidth;
        cellStyle.minWidth = col.minWidth;
      }

      /* Apply styles only if needed */
      if (Object.keys(headerStyle).length) {
        updatedCol.onHeaderCell = () => ({
          style: headerStyle,
        });
      }

      if (Object.keys(cellStyle).length) {
        updatedCol.onCell = () => ({
          style: cellStyle,
        });
      }

      return updatedCol;
    });
  }, [columns]);

  /* ---------- Handle Table Change ---------- */
  const handleChange = (pagination, filters, sorter) => {
    if (clientSideSorting) {
      setSorterState(sorter);
    }

    onChange?.(pagination, filters, sorter);
  };

  /* ---------- Pagination ---------- */
  const resolvedTotal =
    typeof totalRecords === "number" ? totalRecords : sortedData.length;

  const paginationConfig =
    pagination === false
      ? false
      : {
          current: currentPage ?? internalCurrentPage,
          pageSize: internalPageSize,
          total: resolvedTotal,
          showSizeChanger: true,
          pageSizeOptions: ["10", "25", "50", "100"],
          // onChange handles both page change AND size change (Ant Design fires this for both)
          onChange: (page, size) => {
            setInternalCurrentPage(page);
            setInternalPageSize(size);
            onPageChange?.(page, size);
          },
          size: "default",
          ...(showTotal && {
            showTotal: (total, range) =>
              `Showing ${range[0]} – ${range[1]} of ${total} entries`,
          }),
        };

  return (
    <div className={`SwAntDTable ${className}`}>
      <Table
        rowKey={rowKey}
        columns={normalizedColumns}
        dataSource={sortedData}
        loading={{
          spinning: loading,
          indicator: (
            <div className="SwLoaderContainer">
              <SwLoader />
            </div>
          ),
        }}
        pagination={paginationConfig}
        onChange={handleChange}
        rowSelection={rowSelection}
        expandable={expandable}
        bordered={bordered}
        size={size}
        scroll={{ x: scrollX, ...scroll }}
        summary={summary}
        locale={{ emptyText: !loading ? <SwNoData /> : null }}
        showSorterTooltip={showSorterTooltip}
        {...(sortIcon ? { sortIcon } : {})}
        onRow={(record) =>
          onRowClick ? { onClick: () => onRowClick(record) } : {}
        }
      />
    </div>
  );
};

export default AntDTable;
